import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { fmtEuro, MONTHS_FR } from "@/lib/format";
import { PeriodSelector } from "@/components/PeriodSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { MonthlyBudgetTable } from "@/components/MonthlyBudgetTable";
import { BankCashFlow } from "@/components/BankCashFlow";
import { MemberPicker } from "@/components/MemberPicker";
import { Plus, Loader2, ArrowLeft, ShoppingCart, Star, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { date: new Date().toISOString().slice(0, 10), vendor: "", description: "", amount: "", category_id: "", type: "expense", owner: "Commun", region: "", member_ids: [], property_id: "none", loan_id: "none", planned_id: "none", project_id: "none", project_row_id: "none", project_payer: "" };

const KINDS = [
  { key: "courante", label: "Dépense courante", desc: "Une dépense classique du mois", icon: ShoppingCart },
  { key: "lie", label: "Dépense de projet ou exceptionnelle", desc: "Rattachée à un projet ou à une prévision annuelle", icon: Star },
  { key: "revenu", label: "Rentrée / Revenu", desc: "Une entrée d'argent", icon: TrendingUp },
];
const KIND_LABELS = KINDS.reduce((a, k) => { a[k.key] = k.label; return a; }, {});

export default function Monthly() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [txs, setTxs] = useState([]);
  const [planned, setPlanned] = useState([]);
  const [categories, setCategories] = useState([]);
  const [members, setMembers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loanOptions, setLoanOptions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selProject, setSelProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [kind, setKind] = useState("courante");
  const [ver, setVer] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    setVer((v) => v + 1);
    api.get(`/transactions?year=${year}&month=${month}`).then((r) => setTxs(r.data)).finally(() => setLoading(false));
    api.get(`/planned?year=${year}&month=${month}`).then((r) => setPlanned(r.data));
  }, [year, month]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/family-members").then((r) => setMembers(r.data));
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);
  useEffect(load, [load]);

  const openNew = () => { setForm({ ...emptyForm, date: new Date(year, month - 1, 15).toISOString().slice(0, 10) }); setEditId(null); setLoanOptions([]); setSelProject(null); setStep(1); setKind("courante"); setOpen(true); };
  const openEdit = (t) => {
    setForm({ date: t.date.slice(0, 10), vendor: t.vendor, description: t.description, amount: String(t.amount), category_id: t.category_id || "", type: t.type, owner: t.owner || "Commun", region: t.region || "", member_ids: t.member_ids || [], property_id: t.property_id || "none", loan_id: t.loan_id || "none", planned_id: t.planned_id || "none", project_id: t.project_id || "none", project_row_id: t.project_row_id || "none", project_payer: t.project_payer || "" });
    if (t.property_id) api.get(`/properties/${t.property_id}/loans`).then((r) => setLoanOptions(r.data)); else setLoanOptions([]);
    if (t.project_id) api.get(`/projects/${t.project_id}`).then((r) => setSelProject(r.data)); else setSelProject(null);
    setKind(t.type === "income" ? "revenu" : (t.project_id || t.planned_id) ? "lie" : "courante");
    setStep(2); setEditId(t.id); setOpen(true);
  };
  const chooseKind = (k) => {
    setKind(k); setSelProject(null);
    setForm((f) => ({ ...f, type: k === "revenu" ? "income" : "expense", category_id: "", project_id: "none", project_row_id: "none", project_payer: "", planned_id: "none" }));
    setStep(2);
  };

  const projectRows = () => (selProject?.rows || []).filter((r) => (r.kind || "data") === "data");
  const rowLabel = (row) => {
    if (row.label) return row.label;
    const t = (selProject?.columns || []).find((c) => c.type === "text");
    return (t && (row.cells || {})[t.id]) || "Ligne";
  };
  const projectPayerOptions = () => {
    const c = (selProject?.columns || []).find((x) => x.id === selProject?.payer_column_id);
    return (c && c.options && c.options.length) ? c.options : ["Moi", "Ma société"];
  };
  const onProjectChange = async (v) => {
    if (v === "none") { setForm((f) => ({ ...f, project_id: "none", project_row_id: "none", project_payer: "" })); setSelProject(null); return; }
    setForm((f) => ({ ...f, project_id: v, project_row_id: "none", planned_id: "none" }));
    const r = await api.get(`/projects/${v}`);
    setSelProject(r.data);
    setForm((f) => ({ ...f, category_id: r.data.category_id || f.category_id, project_payer: f.project_payer || r.data.payer_me_value || "Moi" }));
  };

  const onCategoryChange = (v) => {
    const cat = categories.find((c) => c.id === v);
    setForm((f) => ({ ...f, category_id: v, member_ids: cat && cat.default_member_ids ? cat.default_member_ids : f.member_ids }));
  };

  const onPropertyChange = (v) => {
    setForm((f) => ({ ...f, property_id: v, loan_id: "none" }));
    if (v && v !== "none") api.get(`/properties/${v}/loans`).then((r) => setLoanOptions(r.data)); else setLoanOptions([]);
  };

  const onPlannedChange = (v) => {
    if (v === "none") { setForm((f) => ({ ...f, planned_id: "none" })); return; }
    const p = planned.find((x) => x.id === v);
    setForm((f) => ({ ...f, planned_id: v, category_id: p && p.category_id ? p.category_id : f.category_id }));
  };
  const onLieChange = (v) => {
    if (v === "none") { setForm((f) => ({ ...f, project_id: "none", project_row_id: "none", project_payer: "", planned_id: "none" })); setSelProject(null); return; }
    if (v.startsWith("project:")) { onProjectChange(v.slice(8)); }
    else { setSelProject(null); setForm((f) => ({ ...f, project_id: "none", project_row_id: "none", project_payer: "" })); onPlannedChange(v.slice(8)); }
  };

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Montant invalide"); return; }
    if (kind === "lie" && form.project_id === "none" && form.planned_id === "none") { toast.error("Choisissez un projet ou une dépense exceptionnelle"); return; }
    if (kind === "lie" && form.project_id !== "none" && form.project_row_id === "none") { toast.error("Choisissez la ligne du projet à rattacher"); return; }
    setSaving(true);
    try {
      const isProj = kind === "lie" && form.project_id !== "none", isExc = kind === "lie" && form.planned_id !== "none";
      const payload = { ...form, amount: Number(form.amount), category_id: form.category_id || null, property_id: form.property_id === "none" ? null : form.property_id, loan_id: form.loan_id === "none" ? null : form.loan_id, planned_id: isExc ? (form.planned_id === "none" ? null : form.planned_id) : null, project_id: isProj ? (form.project_id === "none" ? null : form.project_id) : null, project_row_id: isProj ? (form.project_row_id === "none" ? null : form.project_row_id) : null, project_payer: isProj ? (form.project_payer || null) : null, source: "manual" };
      if (editId) await api.put(`/transactions/${editId}`, payload);
      else await api.post("/transactions", payload);
      toast.success(editId ? "Mouvement modifié" : "Mouvement ajouté");
      setOpen(false); load();
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/transactions/${id}`);
    toast.success("Mouvement supprimé");
    load();
  };

  const grouped = categories.reduce((acc, c) => { (acc[c.type] = acc[c.type] || []).push(c); return acc; }, {});
  const excItems = planned.filter((p) => p.type === "expense" && p.source !== "project");
  const liedValue = form.project_id !== "none" ? `project:${form.project_id}` : form.planned_id !== "none" ? `planned:${form.planned_id}` : "none";

  return (
    <div className="space-y-6" data-testid="monthly-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Dépenses Mensuelles</h1>
          <p className="text-muted-foreground text-sm mt-1">{MONTHS_FR[month - 1]} {year} · {txs.length} mouvements</p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodSelector year={year} month={month} setYear={setYear} setMonth={setMonth} />
          <Button onClick={openNew} data-testid="btn-add-transaction"><Plus className="w-4 h-4 mr-1.5" />Nouveau mouvement</Button>
        </div>
      </div>

      <BankCashFlow year={year} month={month} refreshKey={ver} />

      <MonthlyBudgetTable year={year} month={month} categories={categories} reloadKey={ver} txs={txs} members={members} planned={planned} onEdit={openEdit} onDelete={remove} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="transaction-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Modifier le mouvement" : step === 1 ? "Quel type de mouvement ?" : KIND_LABELS[kind]}</DialogTitle></DialogHeader>
          {step === 1 && !editId && (
            <div className="grid gap-2 py-2" data-testid="tx-kind-step">
              {KINDS.map((k) => (
                <button key={k.key} type="button" onClick={() => chooseKind(k.key)} data-testid={`tx-kind-${k.key}`}
                  className="flex items-start gap-3 rounded-lg border border-border p-3 text-left hover:border-primary hover:bg-secondary/40 transition-colors">
                  <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><k.icon className="w-5 h-5" /></div>
                  <div><div className="font-medium text-sm">{k.label}</div><div className="text-xs text-muted-foreground">{k.desc}</div></div>
                </button>
              ))}
            </div>
          )}
          {(step === 2 || editId) && (<>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="tx-date-input" /></div>
              <div className="space-y-1.5"><Label>Montant (€)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0,00" data-testid="input-transaction-amount" /></div>
            </div>
            <div className="space-y-1.5"><Label>Prestataire</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Ex: Delhaize, TotalEnergies" data-testid="input-transaction-vendor" /></div>
            <div className="space-y-1.5"><Label>Région / Lieu (optionnel)</Label><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Ex: Bruxelles, Espagne" data-testid="tx-region-input" /></div>
            {kind !== "lie" && (
            <div className="space-y-1.5"><Label>Catégorie</Label>
              <Select value={form.category_id} onValueChange={onCategoryChange}>
                <SelectTrigger data-testid="select-transaction-category"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                <SelectContent>
                  {(grouped[form.type] || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-muted-foreground">· {c.group}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            {kind === "lie" && (
              <div className="space-y-1.5 rounded-md border border-border/60 p-3 bg-secondary/20">
                <Label>Lier à une dépense exceptionnelle</Label>
                <Select value={liedValue} onValueChange={onLieChange}>
                  <SelectTrigger data-testid="tx-lie-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {projects.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Projets</SelectLabel>
                        {projects.map((pr) => <SelectItem key={pr.id} value={`project:${pr.id}`}>{pr.name}</SelectItem>)}
                      </SelectGroup>
                    )}
                    {excItems.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Dépenses exceptionnelles</SelectLabel>
                        {excItems.map((p) => <SelectItem key={p.id} value={`planned:${p.id}`}>{p.label} · {fmtEuro(p.amount)}{p.category_name && p.category_name !== "Non catégorisé" ? ` · ${p.category_name}` : ""}</SelectItem>)}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
                {form.project_id !== "none" && (
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div className="space-y-1">
                      <Label className="text-xs">Ligne du projet</Label>
                      <Select value={form.project_row_id} onValueChange={(v) => setForm({ ...form, project_row_id: v })}>
                        <SelectTrigger data-testid="tx-project-row-select"><SelectValue placeholder="Choisir la ligne" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Choisir la ligne…</SelectItem>
                          {projectRows().map((r) => <SelectItem key={r.id} value={r.id}>{rowLabel(r)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Payé par</Label>
                      <Select value={form.project_payer || undefined} onValueChange={(v) => setForm({ ...form, project_payer: v })}>
                        <SelectTrigger data-testid="tx-project-payer-select"><SelectValue placeholder="Payeur" /></SelectTrigger>
                        <SelectContent>{projectPayerOptions().map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground">La catégorie est reprise automatiquement du projet ou de la dépense exceptionnelle sélectionné(e).</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Pour qui ? {form.member_ids.length === 0 && <span className="text-muted-foreground font-normal text-xs">(toute la famille)</span>}</Label>
              <MemberPicker members={members} value={form.member_ids} onChange={(v) => setForm({ ...form, member_ids: v })} testid="tx-member-picker" />
            </div>
            {kind === "courante" && properties.length > 0 && (
              <div className="space-y-1.5"><Label>Bien immobilier lié (optionnel)</Label>
                <Select value={form.property_id} onValueChange={onPropertyChange}>
                  <SelectTrigger data-testid="tx-property-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {loanOptions.length > 0 && (
                  <div className="pt-1">
                    <Label className="text-xs">Emprunt / contrat lié</Label>
                    <Select value={form.loan_id} onValueChange={(v) => setForm({ ...form, loan_id: v })}>
                      <SelectTrigger className="mt-1" data-testid="tx-loan-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {loanOptions.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}{l.loan_bank ? ` · ${l.loan_bank}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground">Si c'est un remboursement de crédit, liez-le au bien et à l'emprunt pour voir le détail intérêts/capital.</p>
              </div>
            )}
            <div className="space-y-1.5"><Label>Note (optionnel)</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tx-desc-input" /></div>
          </div>
          <DialogFooter className="sticky bottom-0 bg-background pt-3 gap-2">
            {!editId && <Button variant="ghost" onClick={() => setStep(1)} data-testid="tx-back-button"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>}
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="save-transaction-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button>
          </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
